import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import AutoxiumLogo from '../components/AutoxiumLogo';

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 404) {
        toast.error(detail || 'This email is not registered.');
      } else if (err.response?.status === 429) {
        toast.error('Too many requests. Please wait a few minutes and try again.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Unable to send reset link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="forgot-password-page">
        <Card className="w-full max-w-md border-2 border-structure shadow-xl">
          <CardContent className="pt-8 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-2 text-chassis">Check Your Email</h2>
            <p className="text-muted-foreground mb-6">
              We've sent a password reset link to <span className="font-semibold text-chassis break-all">{email}</span>.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full h-12 font-bold uppercase tracking-wider border-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border-2 border-structure shadow-xl">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-block mx-auto mb-6">
            <AutoxiumLogo width={80} height={64} />
          </Link>
          <h1 className="font-chivo text-2xl uppercase tracking-tight text-chassis">Forgot Password</h1>
          <p className="text-sm text-muted-foreground">Enter your email to receive a reset link</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 border-2 focus:border-autoxium-blue"
                data-testid="forgot-email-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-autoxium-blue hover:bg-autoxium-blue/90 font-bold uppercase tracking-wider"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-autoxium-blue hover:underline inline-flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
